import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import styled from "styled-components/macro";
import type { ColumnDef } from "@tanstack/react-table";
import { SlideOut } from "../SlideOut/SlideOut";
import { TablePlaceholder } from "../../util/util";
import { useMdmEntityDisplayNames } from "../../util/mdmEntityDisplayNames";
import type { ObjectReferenceResult } from "../../types/types.PIM";
import { TextMdRegular, TextSmRegular } from "../Typography/TypographyV2";
import { Table } from "../Table/Table";
import {
  buildObjectRefPreviewDisplayRows,
  getReferenceRestrictedMessage,
  getObjectReferenceDisplayHref,
  isObjectReferenceDisplayAccessible,
} from "./utils";
import type {
  ReferenceDisplayAccessContext,
  ObjectReferencePathOptions,
} from "./utils";
import { objectRefToProductReference } from "./ObjectReferenceAttributeDisplay";
import { ProductCardsGrid } from "../ProductCardsGrid/ProductCardsGrid";

const NameCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.resolve("spacing_xs")};
  min-width: 0;
  overflow: hidden;
`;

const PreviewList = styled.ul`
  margin: 0;
  padding-left: ${({ theme }) => theme.resolve("spacing_md")};
  list-style: disc;
`;

const PreviewListItem = styled.li`
  margin: 0;
  padding: ${({ theme }) => theme.resolve("spacing_xxs")} 0;
`;

export type ObjectReferenceMoreModalProps = {
  references: ObjectReferenceResult[];
  attributeName: string;
  targetObjectType?: string;
  onClose: () => void;
  linkOptions: ObjectReferencePathOptions;
  accessOptions: ReferenceDisplayAccessContext;
};

export const ObjectReferenceMoreModal = ({
  references,
  attributeName,
  targetObjectType,
  onClose,
  linkOptions,
  accessOptions,
}: ObjectReferenceMoreModalProps) => {
  const { t } = useTranslation();
  const { getObjectTypeLabel } = useMdmEntityDisplayNames();

  const isProductTarget =
    (targetObjectType ?? references[0]?.object_type) === "product";

  const objectType = targetObjectType || references[0]?.object_type;
  const columnHeader = objectType ? getObjectTypeLabel(objectType) : t("Name");

  const productGridItems = useMemo(
    () =>
      references
        .filter((ref) => isObjectReferenceDisplayAccessible(ref, accessOptions))
        .map((ref) => {
          const previewRows = buildObjectRefPreviewDisplayRows(ref, t);
          return {
            id: ref.id,
            product: objectRefToProductReference(ref),
            href: getObjectReferenceDisplayHref(ref, linkOptions),
            dynamicPreviewBlocks: previewRows.map((row) => ({
              header: row.label,
              content: row.value,
            })),
          };
        }),
    [references, accessOptions, linkOptions, t]
  );

  const inaccessibleProductItems = useMemo(
    () =>
      references
        .filter(
          (ref) => !isObjectReferenceDisplayAccessible(ref, accessOptions)
        )
        .map((ref) => ({
          id: ref.id,
          product: objectRefToProductReference(ref),
          message: getReferenceRestrictedMessage(ref, t),
        })),
    [references, accessOptions, t]
  );

  const columns = useMemo(
    (): ColumnDef<ObjectReferenceResult>[] => [
      {
        id: "name",
        header: columnHeader,
        cell: ({ row }) => {
          const ref = row.original;
          const accessible = isObjectReferenceDisplayAccessible(
            ref,
            accessOptions
          );
          const previewRows = accessible
            ? buildObjectRefPreviewDisplayRows(ref, t)
            : [];
          const href = accessible
            ? getObjectReferenceDisplayHref(ref, linkOptions)
            : null;

          return (
            <NameCell>
              <TextMdRegular>
                {href ? <Link to={href}>{ref.name}</Link> : ref.name}
              </TextMdRegular>
              {accessible && previewRows.length > 0 ? (
                <PreviewList>
                  {previewRows.map((row) => (
                    <PreviewListItem key={`${row.label}-${row.value}`}>
                      <TextSmRegular color="tertiary">
                        {row.label}: {row.value}
                      </TextSmRegular>
                    </PreviewListItem>
                  ))}
                </PreviewList>
              ) : (
                !accessible && (
                  <TextSmRegular color="tertiary">
                    {getReferenceRestrictedMessage(ref, t)}
                  </TextSmRegular>
                )
              )}
            </NameCell>
          );
        },
      },
    ],
    [columnHeader, linkOptions, accessOptions, t]
  );

  return (
    <SlideOut title={attributeName} show closeFlyout={onClose}>
      {!references.length ? (
        <TablePlaceholder message={t("No items to show.")} />
      ) : isProductTarget ? (
        <ProductCardsGrid
          items={productGridItems}
          inaccessibleItems={inaccessibleProductItems}
        />
      ) : (
        <Table
          columns={columns}
          data={references}
          isLoading={false}
          error={undefined}
          disableTableSelection
          lastChildleftAlign
          Placeholder={<TablePlaceholder message={t("No items to show.")} />}
        />
      )}
    </SlideOut>
  );
};
